import { setContext } from '@apollo/client/link/context';
import Cookies from 'universal-cookie';
import { applyDefaultAppActiveContextHeader } from '../api/appActiveContext';
import { CSRF_COOKIE_NAME, GRAPHQL_CONSISTENCY_TOKENS_ENABLED } from '../constants/environment';
import { IMPERSONATED_KEY } from '../constants/sessionStorage';
import { safeSessionStorage } from 'src/lib/browserStorage';
import { getBrowserTimeZone } from 'src/lib/browserTimeZone';
import { CONSISTENCY_TOKEN_HEADER, getConsistencyTokenHeaderValue } from './graphqlConsistency';

const hasInvalidHeaderChars = (value: string) => /[\r\n]/.test(value);

const toSafeHeaderValue = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const valueString = String(value);
    if (!valueString || hasInvalidHeaderChars(valueString)) {
        return null;
    }
    return valueString;
};

const toSafeHeaders = (headers: unknown): Record<string, string> => {
    // Validate using the native Headers API, but return a plain object.
    // Apollo HttpLink merges context headers as plain objects; returning a Headers
    // instance here can break that merge behavior and lead to missing auth/CSRF
    // headers (we saw /api/graphql/ 403s when returning Headers directly).
    const validatedHeaders = new Headers();
    const appendHeader = (name: unknown, value: unknown) => {
        if (typeof name !== 'string' || !name) {
            return;
        }
        const safeValue = toSafeHeaderValue(value);
        if (!safeValue) {
            return;
        }
        try {
            validatedHeaders.set(name, safeValue);
        } catch {
            // Ignore invalid header names/values.
        }
    };

    if (!headers) {
        return {};
    }

    if (headers instanceof Headers) {
        headers.forEach((value, key) => appendHeader(key, value));
    } else if (Array.isArray(headers)) {
        headers.forEach((entry) => {
            if (Array.isArray(entry) && entry.length === 2) {
                appendHeader(entry[0], entry[1]);
            }
        });
    } else if (typeof headers === 'object') {
        Object.entries(headers as Record<string, unknown>).forEach(([name, value]) => {
            appendHeader(name, value);
        });
    }

    const safeHeaders: Record<string, string> = {};
    validatedHeaders.forEach((value, key) => {
        safeHeaders[key] = value;
    });
    return safeHeaders;
};
const cookies = new Cookies();

export const createCustomHeadersLink = () =>
    setContext((_, { headers, fetchOptions }) => {
        const safeHeaders = toSafeHeaders(headers);
        const csrfToken = toSafeHeaderValue(cookies.get(CSRF_COOKIE_NAME));
        if (csrfToken) {
            safeHeaders['X-CSRFToken'] = csrfToken;
        }
        const impersonated = toSafeHeaderValue(safeSessionStorage.getItem(IMPERSONATED_KEY));
        if (impersonated) {
            safeHeaders['X-Impersonate'] = impersonated;
        }
        applyDefaultAppActiveContextHeader(safeHeaders);
        const browserTimeZone = toSafeHeaderValue(getBrowserTimeZone());
        if (browserTimeZone) {
            safeHeaders.TZ = browserTimeZone;
        }
        let consistencyTokens: string | null = null;
        if (GRAPHQL_CONSISTENCY_TOKENS_ENABLED) {
            try {
                consistencyTokens = toSafeHeaderValue(getConsistencyTokenHeaderValue());
            } catch (error) {
                console.warn('Failed to read request consistency tokens.', error);
            }
        }
        if (consistencyTokens) {
            safeHeaders[CONSISTENCY_TOKEN_HEADER] = consistencyTokens;
        }

        return {
            headers: safeHeaders,
            fetchOptions: {
                ...fetchOptions,
                referrerPolicy: 'strict-origin-when-cross-origin',
            },
        };
    });
